import { useEffect, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../../contexts/ThemeContext';
import { MapPropertyTooltip } from './MapPropertyTooltip';
import '../../css/worldMap.css';

export interface MapPropertyTooltipData {
  unitId: number;
  imageUrl: string;
  fallbackImageUrl?: string;
  unitType?: string;
  title: string;
  country?: string;
  currency?: string;
  operatingModel?: string;
  description?: string;
}

export interface LocationData {
  id: string;
  lat: number;
  lng: number;
  value: number;
  label?: string;
  color?: string;
  /** When set, tooltips/popups show this instead of a currency value (e.g. risk coverage). */
  detail?: string;
  /** Rich property preview shown on marker hover (overview portfolio map). */
  property?: MapPropertyTooltipData;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

interface WorldMapProps {
  locations: LocationData[];
  height?: number;
  className?: string;
  onLocationClick?: (location: LocationData) => void;
  onPropertyViewDetails?: (property: MapPropertyTooltipData) => void;
}

interface MapTooltipState {
  location: LocationData;
  x: number;
  y: number;
}

const PROPERTY_TOOLTIP_WIDTH = 200;
const SIMPLE_TOOLTIP_WIDTH = 180;
const TOOLTIP_OFFSET = 12;

function getMarkerClientPoint(
  map: L.Map,
  mapElement: HTMLDivElement,
  lat: number,
  lng: number,
) {
  const point = map.latLngToContainerPoint([lat, lng]);
  const rect = mapElement.getBoundingClientRect();
  return {
    x: rect.left + point.x,
    y: rect.top + point.y,
  };
}

/** Anchor tooltip to the map pin; height changes grow upward, not shifting the anchor. */
function getAnchoredTooltipPosition(
  anchorX: number,
  anchorY: number,
  width: number,
) {
  let left = anchorX + TOOLTIP_OFFSET;
  if (left + width > window.innerWidth - 8) {
    left = anchorX - width - TOOLTIP_OFFSET;
  }
  left = Math.max(8, left);

  const placeBelow = anchorY < 140;

  if (placeBelow) {
    return {
      left: `${left}px`,
      top: `${anchorY + TOOLTIP_OFFSET}px`,
      transform: 'translateY(0)',
    };
  }

  return {
    left: `${left}px`,
    top: `${anchorY - TOOLTIP_OFFSET}px`,
    transform: 'translateY(-100%)',
  };
}

function attachMapResizeHandling(
  map: L.Map,
  mapElement: HTMLDivElement,
) {
  const invalidateMapSize = () => {
    map.invalidateSize();
  };

  const resizeObserver = new ResizeObserver(invalidateMapSize);
  resizeObserver.observe(mapElement);
  const parentElement = mapElement.parentElement;
  if (parentElement) {
    resizeObserver.observe(parentElement);
  }

  window.addEventListener('resize', invalidateMapSize);
  window.addEventListener('orientationchange', invalidateMapSize);

  const timeoutId = window.setTimeout(invalidateMapSize, 150);
  const delayedTimeoutId = window.setTimeout(invalidateMapSize, 500);

  return () => {
    window.clearTimeout(timeoutId);
    window.clearTimeout(delayedTimeoutId);
    resizeObserver.disconnect();
    window.removeEventListener('resize', invalidateMapSize);
    window.removeEventListener('orientationchange', invalidateMapSize);
  };
}

const createIcon = (primaryColor: string, gradientColor: string) => {
  const safePrimaryColor = primaryColor.replace(/[<>"'`]/g, '');
  const safeGradientColor = gradientColor.replace(/[<>"'`]/g, '');
  return L.divIcon({
    className: 'world-map-marker',
    html: `
      <div class="world-map-marker__wrap">
        <span class="world-map-marker__pulse" style="--marker-color: ${safePrimaryColor}" aria-hidden="true"></span>
        <svg class="world-map-marker__pin" viewBox="0 0 24 34" width="22" height="31" aria-hidden="true">
          <defs>
            <linearGradient id="world-map-pin-gradient" x1="3" y1="2" x2="21" y2="32" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="${safeGradientColor}" />
              <stop offset="100%" stop-color="${safePrimaryColor}" />
            </linearGradient>
          </defs>
          <path
            fill="url(#world-map-pin-gradient)"
            d="M12 0C5.925 0 1 4.925 1 11c0 8.284 9.12 20.687 10.34 22.287a.9.9 0 0 0 1.32 0C13.88 31.687 23 19.284 23 11 23 4.925 18.075 0 12 0z"
          />
          <circle cx="12" cy="11" r="4.25" fill="#fff"/>
        </svg>
      </div>
    `,
    iconSize: [22, 31],
    iconAnchor: [11, 30],
    popupAnchor: [0, -31],
  });
};

function getLocationSubtitle(location: LocationData) {
  if (location.detail) return location.detail.slice(0, 200);
  return `$${location.value.toLocaleString()}`;
}

export const WorldMap = ({
  locations,
  height,
  className = '',
  onLocationClick,
  onPropertyViewDetails,
}: WorldMapProps) => {
  const { theme } = useTheme();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [hoverState, setHoverState] = useState<MapTooltipState | null>(null);
  const [pinnedState, setPinnedState] = useState<MapTooltipState | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    const mapOptions = {
      center: [20, 0] as L.LatLngExpression,
      zoom: 2,
      zoomControl: true,
      scrollWheelZoom: !L.Browser.mobile,
      doubleClickZoom: true,
      boxZoom: false,
      dragging: true,
      keyboard: true,
      touchZoom: true,
    };

    setHoverState(null);
    setPinnedState(null);

    if (locations.length === 0) {
      const map = L.map(mapRef.current, mapOptions);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
        opacity: 0.7
      }).addTo(map);

      mapInstanceRef.current = map;

      const cleanupResize = attachMapResizeHandling(map, mapRef.current);

      return () => {
        cleanupResize();
        setHoverState(null);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
      };
    }

    const map = L.map(mapRef.current, mapOptions);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
      opacity: 0.7
    }).addTo(map);

    L.rectangle(
      [[-90, -180], [90, 180]],
      {
        color: 'transparent',
        fillColor: 'transparent',
        fillOpacity: 0,
        className: 'dotted-overlay'
      }
    ).addTo(map);

    const markers: L.Marker[] = [];
    const clearHoverTooltip = () => setHoverState(null);

    locations.forEach((location) => {
      const marker = L.marker(
        [location.lat, location.lng],
        {
          icon: createIcon(theme.colors.primary, theme.colors.tertiary)
        }
      ).addTo(map);

      const anchorToMarker = () => {
        if (!mapRef.current) return null;
        const { x, y } = getMarkerClientPoint(map, mapRef.current, location.lat, location.lng);
        return { location, x, y };
      };

      const showHoverTooltip = () => {
        const state = anchorToMarker();
        if (state) setHoverState(state);
      };

      marker.on('mouseover', showHoverTooltip);
      marker.on('mouseout', clearHoverTooltip);

      marker.on('click', () => {
        clearHoverTooltip();

        if (location.property) {
          const state = anchorToMarker();
          if (state) setPinnedState(state);
          return;
        }

        setPinnedState(null);
        onLocationClick?.(location);
      });

      if (!location.property) {
        const popupBody = location.detail
          ? `<p style="margin: 0; color: var(--color-text-secondary); font-size: 13px; text-align: left; max-width: 240px;">${escapeHtml(location.detail.slice(0, 400))}</p>`
          : `<p style="margin: 0; color: var(--color-text-secondary); font-size: 14px;">Value: <strong>$${location.value.toLocaleString()}</strong></p>`;

        marker.bindPopup(`
          <div style="text-align: center; padding: 8px;">
            <h4 style="margin: 0 0 8px 0; color: var(--color-text);">${escapeHtml(location.label || 'Location')}</h4>
            ${popupBody}
          </div>
        `);
      }

      markers.push(marker);
    });

    map.on('movestart zoomstart', clearHoverTooltip);

    const syncPinnedAnchor = () => {
      if (!mapRef.current) return;
      setPinnedState((prev) => {
        if (!prev) return prev;
        const { x, y } = getMarkerClientPoint(
          map,
          mapRef.current!,
          prev.location.lat,
          prev.location.lng,
        );
        return { ...prev, x, y };
      });
    };

    map.on('move zoom', syncPinnedAnchor);

    const fitMarkers = () => {
      if (markers.length === 0) return;

      const group = new L.FeatureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.45), { maxZoom: 12 });

      if (markers.length === 1) {
        map.panBy([0, -14], { animate: false });
      }
    };

    fitMarkers();
    const initialRefitTimeoutId = window.setTimeout(() => {
      map.invalidateSize();
      fitMarkers();
    }, 150);

    const cleanupResize = attachMapResizeHandling(map, mapRef.current);

    mapInstanceRef.current = map;

    return () => {
      window.clearTimeout(initialRefitTimeoutId);
      cleanupResize();
      clearHoverTooltip();
      map.off('movestart zoomstart', clearHoverTooltip);
      map.off('move zoom', syncPinnedAnchor);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [locations, onLocationClick, theme.colors.primary, theme.colors.tertiary]);

  const hoverProperty = hoverState?.location.property;
  const pinnedProperty = pinnedState?.location.property;
  const showHoverPropertyTooltip = Boolean(hoverProperty && !pinnedState);
  const showPinnedPropertyTooltip = Boolean(pinnedProperty && pinnedState);
  const showSimpleTooltip = Boolean(hoverState && !hoverProperty && !pinnedState);

  const hoverPropertyTooltipStyle = showHoverPropertyTooltip && hoverState
    ? getAnchoredTooltipPosition(
        hoverState.x,
        hoverState.y,
        PROPERTY_TOOLTIP_WIDTH,
      )
    : undefined;

  const pinnedPropertyTooltipStyle = showPinnedPropertyTooltip && pinnedState
    ? getAnchoredTooltipPosition(
        pinnedState.x,
        pinnedState.y,
        PROPERTY_TOOLTIP_WIDTH,
      )
    : undefined;

  const simpleTooltipStyle = showSimpleTooltip && hoverState
    ? getAnchoredTooltipPosition(
        hoverState.x,
        hoverState.y,
        SIMPLE_TOOLTIP_WIDTH,
      )
    : undefined;

  return (
    <div
      className="world-map-shell"
      style={{ height: typeof height === 'number' ? `${height}px` : '100%' }}
    >
      <div
        className={`world-map ${className}`}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid var(--color-border)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div
          ref={mapRef}
          style={{
            height: '100%',
            width: '100%',
          }}
        />
      </div>
      {showHoverPropertyTooltip && hoverProperty && hoverState &&
        createPortal(
          <MapPropertyTooltip
            property={hoverProperty}
            style={hoverPropertyTooltipStyle}
          />,
          document.body,
        )}
      {showPinnedPropertyTooltip && pinnedProperty && pinnedState &&
        createPortal(
          <MapPropertyTooltip
            property={pinnedProperty}
            pinned
            style={pinnedPropertyTooltipStyle}
            onClose={() => setPinnedState(null)}
            onViewDetails={onPropertyViewDetails}
          />,
          document.body,
        )}
      {showSimpleTooltip && hoverState &&
        createPortal(
          <div className="world-map-hover-tooltip" style={simpleTooltipStyle} role="tooltip">
            <div className="world-map-hover-tooltip__title">
              {hoverState.location.label || 'Location'}
            </div>
            <div className="world-map-hover-tooltip__subtitle">
              {getLocationSubtitle(hoverState.location)}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
